import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../contexts/ThemeContext';
import {
    Box,
    Container,
    Typography,
    TextField,
    InputAdornment,
    Card,
    CardContent,
    Grid,
    Chip,
    IconButton,
    Avatar,
    LinearProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
    Alert,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Stack,
    Badge
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Event as EventIcon,
    TrendingUp as TrendingUpIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Schedule as ScheduleIcon,
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    History as HistoryIcon,
    EventBusy as EventBusyIcon,
    GridView as GridViewIcon,
    ViewList as ViewListIcon,
    InfoOutlined as InfoIcon,
    AccountBalance as BusinessIcon,
    Star as StarIcon,
    LocationOn as LocationIcon,
    Badge as IDBadgeIcon,
    Comment as NotesIcon,
    BarChart as BarChartIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Add as AddIcon,
    EventAvailable as EventAvailableIcon
} from '@mui/icons-material';
import ReservationEditDialog from '../components/board/ReservationEditDialog';
import AddReservationPopover from '../components/AddReservationPopover';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface Client {
    id: number;
    name: string;
    email: string;
    phone: string;
    alternative_phone?: string;
    dni?: string;
    address?: string;
    notes?: string;
    businessId: number;
    createdAt: string;
    updatedAt: string;
    business?: {
        id: number;
        name: string;
    };
    stats: {
        totalReservations: number;
        confirmedReservations: number;
        cancelledReservations: number;
        pendingReservations: number;
        cancellationRate: number;
        lastReservationDate?: string;
    };
    reservations?: Reservation[];
}

interface Reservation {
    id: number;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    startDate: string;
    endDate: string;
    status: 'pending' | 'confirmed' | 'cancelled';
    paymentStatus?: 'pending' | 'partial' | 'paid' | 'refunded';
    notes?: string;
    rescheduleDate?: string;
    createdAt: string;
    board?: {
        id: number;
        name: string;
        type: string;
    };
}

const Clients: React.FC = () => {
    const { mode: themeMode } = useThemeMode();
    const [clients, setClients] = useState<Client[]>([]);
    const [filteredClients, setFilteredClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterBusiness, setFilterBusiness] = useState<string>('all');
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [editClient, setEditClient] = useState<Client | null>(null);
    const [editing, setEditing] = useState(false);
    const [viewMode, setViewMode] = useState<'grid' | 'list' | 'details' | 'grouped'>('grid');
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [detailTab, setDetailTab] = useState(0);
    const [editReservation, setEditReservation] = useState<Reservation | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [cancelFutureDialogOpen, setCancelFutureDialogOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [addResAnchor, setAddResAnchor] = useState<null | HTMLElement>(null);
    const [availableBoards, setAvailableBoards] = useState<any[]>([]);

    const isDark = themeMode === 'dark';

    const handleOpenAddRes = async (event: React.MouseEvent<HTMLElement>, client: Client) => {
        event.stopPropagation();
        setSelectedClient(client);
        setAddResAnchor(event.currentTarget);
        
        try {
            const response = await axios.get(`/api/businesses/${client.businessId}/boards`);
            setAvailableBoards(response.data);
        } catch (error) {
            console.error('Error fetching boards:', error);
        }
    };

    // Fetch all clients
    useEffect(() => {
        fetchClients();
    }, []);

    const fetchClients = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/clients');
            setClients(response.data);
            setFilteredClients(response.data);
        } catch (error) {
            console.error('Error fetching clients:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter and search logic
    useEffect(() => {
        let filtered = clients;

        // Search filter
        if (searchQuery) {
            filtered = filtered.filter(client =>
                client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                client.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                client.phone?.includes(searchQuery)
            );
        }

        // Business filter
        if (filterBusiness !== 'all') {
            filtered = filtered.filter(client => client.businessId.toString() === filterBusiness);
        }

        setFilteredClients(filtered);
    }, [searchQuery, filterBusiness, clients]);

    const handleClientClick = async (client: Client, tabIndex = 0) => {
        try {
            // Fetch full client details with reservations
            const response = await axios.get(`/api/clients/${client.id}`);
            setSelectedClient(response.data);
            setDetailTab(tabIndex);
            setDetailDialogOpen(true);
        } catch (error) {
            console.error('Error fetching client details:', error);
        }
    };

    const handleCloseDetail = () => {
        setDetailDialogOpen(false);
        setSelectedClient(null);
        setDetailTab(0);
        setEditClient(null);
        setEditing(false);
    };

    // Get unique businesses for filter
    const businesses = Array.from(new Set(clients.map(c => c.business?.name).filter(Boolean)));

    // Calculate global statistics
    const totalClients = clients.length;
    const totalReservations = clients.reduce((sum, c) => sum + (c.stats?.totalReservations || 0), 0);
    const avgReservationsPerClient = totalClients > 0 ? (totalReservations / totalClients).toFixed(1) : '0';
    const avgCancellationRate = totalClients > 0
        ? (clients.reduce((sum, c) => sum + (c.stats?.cancellationRate || 0), 0) / totalClients).toFixed(1)
        : '0';

    const refreshSelectedClient = async () => {
        if (selectedClient) {
            try {
                const response = await axios.get(`/api/clients/${selectedClient.id}`);
                setSelectedClient(response.data);
                // Also update the client in the main list if needed
                setClients(prev => prev.map(c => c.id === selectedClient.id ? { ...c, stats: response.data.stats } : c));
            } catch (error) {
                console.error('Error refreshing client:', error);
            }
        }
    };

    const handleEditClick = (reservation: Reservation) => {
        setEditReservation(reservation);
        setEditDialogOpen(true);
    };

    const handleEditClose = () => {
        setEditDialogOpen(false);
        setEditReservation(null);
    };

    const handleCancelFutureClick = () => {
        setCancelFutureDialogOpen(true);
    };

    const handleConfirmCancelFuture = async () => {
        if (!selectedClient) return;

        try {
            await axios.post(`/api/clients/${selectedClient.id}/cancel-future`, {
                reason: cancelReason
            });

            // Refresh client data
            await refreshSelectedClient();

            // Close dialogs
            setCancelFutureDialogOpen(false);
            setCancelReason('');

            // Show success alert (optional, but good UX)
            alert('Reservas futuras canceladas exitosamente');
        } catch (error) {
            console.error('Error cancelling future reservations:', error);
            alert('Error al cancelar reservas');
        }
    };

    const handleEditSave = async (updates: any) => {
        try {
            if (editReservation) {
                await axios.put(`/api/reservations/${editReservation.id}`, updates);
                await refreshSelectedClient();
                handleEditClose();
            }
        } catch (error) {
            console.error('Error updating reservation:', error);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'confirmed': return 'success';
            case 'cancelled': return 'error';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'confirmed': return <CheckCircleIcon fontSize="small" />;
            case 'cancelled': return <CancelIcon fontSize="small" />;
            case 'pending': return <ScheduleIcon fontSize="small" />;
            default: return null;
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* View Mode & Filter Bar */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: 2, justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.5px' }}>
                        Directorio de Clientes
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', opacity: 0.8 }}>
                        Explora y gestiona tu base de datos de clientes corporativos
                    </Typography>
                </Box>
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(_, v) => v && setViewMode(v)}
                    size="small"
                    sx={{
                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '12px',
                        '& .MuiToggleButton-root': {
                            px: 2,
                            py: 1,
                            borderRadius: '10px !important',
                            border: 'none',
                            m: 0.5,
                            transition: 'all 0.2s',
                            '&.Mui-selected': {
                                backgroundColor: isDark ? 'white' : '#1a1a1a',
                                color: isDark ? '#1a1a1a' : 'white',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '&:hover': {
                                    backgroundColor: isDark ? '#e0e0e0' : '#333'
                                }
                            }
                        }
                    }}
                >
                    <ToggleButton value="grid">
                        <GridViewIcon sx={{ mr: 1, fontSize: 20 }} />
                        Grilla
                    </ToggleButton>
                    <ToggleButton value="list">
                        <ViewListIcon sx={{ mr: 1, fontSize: 20 }} />
                        Lista
                    </ToggleButton>
                    <ToggleButton value="grouped">
                        <BusinessIcon sx={{ mr: 1, fontSize: 20 }} />
                        Por Negocio
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Statistics Dashboard */}
            <Grid container spacing={3} sx={{ mb: 5 }}>
                {[
                    { label: 'Total Clientes', value: totalClients, icon: <PersonIcon />, color: '#6366f1', grad: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)' },
                    { label: 'Total Reservas', value: totalReservations, icon: <EventIcon />, color: '#ec4899', grad: 'linear-gradient(135deg, #f43f5e 0%, #fb923c 100%)' },
                    { label: 'Fidelidad', value: `${avgReservationsPerClient} res/cl`, icon: <StarIcon />, color: '#0ea5e9', grad: 'linear-gradient(135deg, #0ea5e9 0%, #22d3ee 100%)' },
                    { label: 'Tasa Cancel.', value: `${avgCancellationRate}%`, icon: <CancelIcon />, color: '#f59e0b', grad: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }
                ].map((stat, idx) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
                        <Card sx={{
                            height: '100%',
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: 4,
                            background: isDark ? 'rgba(30, 30, 30, 0.6)' : '#fff',
                            backdropFilter: 'blur(20px)',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                            boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.3)' : '0 10px 25px rgba(0,0,0,0.04)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-6px)',
                                boxShadow: `0 12px 40px ${isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.08)'}`,
                                '& .stat-grad': { opacity: 1 }
                            }
                        }}>
                            <Box className="stat-grad" sx={{
                                position: 'absolute',
                                top: 0, left: 0, right: 0,
                                height: '4px',
                                background: stat.grad,
                                opacity: 0.7,
                                transition: 'opacity 0.3s'
                            }} />
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                                            {stat.label}
                                        </Typography>
                                        <Typography variant="h3" sx={{ fontWeight: 800 }}>
                                            {stat.value}
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{
                                        bgcolor: 'transparent',
                                        color: stat.color,
                                        width: 48, height: 48,
                                        border: `1.5px solid ${stat.color}40`,
                                        '& svg': { fontSize: 26 }
                                    }}>
                                        {stat.icon}
                                    </Avatar>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Premium Filter Bar */}
            <Box sx={{
                mb: 4,
                p: 2,
                borderRadius: 4,
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#e2e8f0'}`,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                alignItems: 'center'
            }}>
                <Box sx={{ position: 'relative', flex: 1, width: '100%' }}>
                    <TextField
                        fullWidth
                        placeholder="Buscar por nombre, email o teléfono..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.secondary', ml: 1 }} />
                                </InputAdornment>
                            ),
                            sx: {
                                borderRadius: 3,
                                backgroundColor: isDark ? '#1a1a1a' : '#fff',
                                height: 52,
                                '& fieldset': { border: 'none' },
                                boxShadow: isDark ? 'inset 0 2px 4px rgba(0,0,0,0.2)' : 'inset 0 1px 2px rgba(0,0,0,0.05)',
                            }
                        }}
                    />
                </Box>
                <FormControl sx={{ minWidth: 240, width: { xs: '100%', md: 'auto' } }}>
                    <Select
                        value={filterBusiness}
                        onChange={(e) => setFilterBusiness(e.target.value)}
                        displayEmpty
                        startAdornment={
                            <InputAdornment position="start">
                                <FilterIcon sx={{ color: 'text.secondary', ml: 1 }} />
                            </InputAdornment>
                        }
                        sx={{
                            borderRadius: 3,
                            backgroundColor: isDark ? '#1a1a1a' : '#fff',
                            height: 52,
                            '& fieldset': { border: 'none' },
                            boxShadow: isDark ? 'inset 0 2px 4px rgba(0,0,0,0.2)' : 'inset 0 1px 2px rgba(0,0,0,0.05)',
                        }}
                    >
                        <MenuItem value="all">Todos los Negocios</MenuItem>
                        {businesses.map((business, index) => (
                            <MenuItem key={index} value={clients.find(c => c.business?.name === business)?.businessId.toString() || ''}>
                                {business}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Clients List based on View Mode */}
            {loading ? (
                <Box sx={{ width: '100%', py: 10, textAlign: 'center' }}>
                    <LinearProgress sx={{ maxWidth: 400, mx: 'auto', height: 8, borderRadius: 4 }} />
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontWeight: 600 }}>Cargando directorio de clientes...</Typography>
                </Box>
            ) : filteredClients.length === 0 ? (
                <Box sx={{ 
                    py: 12, textAlign: 'center', 
                    bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                    borderRadius: 8, border: `2px dashed ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
                }}>
                    <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 3, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: 'text.secondary' }}>
                        <SearchIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>No se encontraron clientes</Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 400, mx: 'auto' }}>
                        Intenta ajustar los filtros o la búsqueda para encontrar lo que estás buscando.
                    </Typography>
                </Box>
            ) : (
                <>
                    {viewMode === 'grid' && (
                        <Grid container spacing={3}>
                            {filteredClients.map((client) => (
                                <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={client.id}>
                                    <Card
                                        sx={{
                                            position: 'relative',
                                            borderRadius: 4,
                                            overflow: 'hidden',
                                            background: isDark ? 'rgba(30,30,30,0.6)' : '#fff',
                                            backdropFilter: 'blur(10px)',
                                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'}`,
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                transform: 'translateY(-6px)',
                                                boxShadow: isDark ? '0 20px 40px rgba(0,0,0,0.6)' : '0 15px 35px rgba(0,0,0,0.06)',
                                                '& .client-actions': { opacity: 1, transform: 'translateY(0)' }
                                            }
                                        }}
                                        onClick={() => handleClientClick(client)}
                                    >
                                        <Box sx={{ p: 3, pt: 4, pb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                                                <Badge
                                                    overlap="circular"
                                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                    badgeContent={
                                                        client.stats.cancellationRate < 10 ? <Tooltip title="Cliente Excelente"><StarIcon sx={{ fontSize: 16, color: '#f59e0b' }} /></Tooltip> : null
                                                    }
                                                >
                                                    <Avatar
                                                        sx={{
                                                            width: 64,
                                                            height: 64,
                                                            background: `linear-gradient(135deg, #6366f1 0%, #a855f7 100%)`,
                                                            boxShadow: '0 8px 16px rgba(99, 102, 241, 0.3)',
                                                            fontSize: '1.5rem',
                                                            fontWeight: 700
                                                        }}
                                                    >
                                                        {client.name.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                </Badge>
                                                <Box sx={{ ml: 2, flex: 1 }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 0.5 }}>
                                                        {client.name}
                                                    </Typography>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                            {client.business?.name || 'Independiente'}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            </Box>

                                            <Stack spacing={1.5} sx={{ mb: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <EmailIcon sx={{ fontSize: 16, mr: 1.5, color: '#6366f1' }} />
                                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>{client.email}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <PhoneIcon sx={{ fontSize: 16, mr: 1.5, color: '#10b981' }} />
                                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>{client.phone || 'Sin teléfono'}</Typography>
                                                </Box>
                                            </Stack>

                                            <Box sx={{ 
                                                display: 'flex', 
                                                gap: 1.5, 
                                                p: 2, 
                                                borderRadius: 3, 
                                                backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#f8fafc',
                                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#e2e8f0'}`
                                            }}>
                                                <Box sx={{ flex: 1, textAlign: 'center' }}>
                                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                                        Reservas
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{client.stats.totalReservations}</Typography>
                                                </Box>
                                                <Divider orientation="vertical" flexItem sx={{ height: 20, my: 'auto', alignSelf: 'center' }} />
                                                <Box sx={{ flex: 1, textAlign: 'center' }}>
                                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                                        Cancelación
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ 
                                                        fontWeight: 800, 
                                                        color: client.stats.cancellationRate > 25 ? '#ef4444' : '#10b981'
                                                    }}>
                                                        {client.stats.cancellationRate}%
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Actions Floating Pane */}
                                        <Box className="client-actions" sx={{
                                            position: 'absolute',
                                            top: 12, right: 12,
                                            display: 'flex',
                                            gap: 1,
                                            opacity: { xs: 1, md: 0 },
                                            transform: { xs: 'translateY(0)', md: 'translateY(-10px)' },
                                            transition: 'all 0.3s'
                                        }}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Tooltip title="Nueva Reserva">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={(e) => handleOpenAddRes(e, client)}
                                                        sx={{ bgcolor: 'success.main', color: 'white', '&:hover': { bgcolor: 'success.dark' } }}
                                                    >
                                                        <AddIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Ver Historial">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleClientClick(client, 1)}
                                                        sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
                                                    >
                                                        <HistoryIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Editar Datos">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleClientClick(client, 0)}
                                                        sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    {viewMode === 'list' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {filteredClients.map((client) => (
                                <Card
                                    key={client.id}
                                    sx={{
                                        position: 'relative',
                                        borderRadius: 3,
                                        background: isDark ? 'rgba(30,30,30,0.6)' : '#fff',
                                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}`,
                                        transition: 'all 0.2s',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'scale(1.005)',
                                            boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 4px 15px rgba(0,0,0,0.05)',
                                            borderColor: '#6366f140'
                                        }
                                    }}
                                    onClick={() => handleClientClick(client)}
                                >
                                    <CardContent sx={{ p: '12px 20px !important' }}>
                                        <Grid container alignItems="center" spacing={2}>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Avatar
                                                        sx={{
                                                            width: 40, height: 40,
                                                            background: `linear-gradient(135deg, #6366f1 0%, #a855f7 100%)`,
                                                            mr: 2,
                                                            fontSize: '1rem',
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        {client.name.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>{client.name}</Typography>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <BusinessIcon sx={{ fontSize: 12 }} /> {client.business?.name || 'Particular'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 3 }}>
                                                <Stack spacing={0.25}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="body2" noWrap>{client.email}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="body2">{client.phone || '-'}</Typography>
                                                    </Box>
                                                </Stack>
                                            </Grid>
                                            <Grid size={{ xs: 6, md: 2 }}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, fontSize: '0.6rem', textTransform: 'uppercase' }}>
                                                        Reservas
                                                    </Typography>
                                                    <Chip label={client.stats.totalReservations} size="small" sx={{ fontWeight: 700, height: 20 }} />
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 6, md: 2 }}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, fontSize: '0.6rem', textTransform: 'uppercase' }}>
                                                        Cancelación
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 800, color: client.stats.cancellationRate > 25 ? '#ef4444' : '#10b981' }}>
                                                        {client.stats.cancellationRate}%
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenAddRes(e, client); }}>
                                                        <AddIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleClientClick(client, 0); }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}

                    {viewMode === 'grouped' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {businesses.map((businessName) => {
                                const businessClients = filteredClients.filter(c => c.business?.name === businessName);
                                if (businessClients.length === 0) return null;

                                return (
                                    <Box key={businessName}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
                                                {businessName}
                                            </Typography>
                                            <Chip 
                                                label={`${businessClients.length} ${businessClients.length === 1 ? 'cliente' : 'clientes'}`} 
                                                size="small" 
                                                variant="outlined" 
                                                sx={{ fontWeight: 600, borderColor: 'primary.main', color: 'primary.main' }} 
                                            />
                                        </Box>
                                        <Grid container spacing={3}>
                                            {businessClients.map((client) => (
                                                <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={client.id}>
                                                    <Card
                                                        sx={{
                                                            position: 'relative',
                                                            borderRadius: 4,
                                                            background: isDark ? 'rgba(30,30,30,0.6)' : '#fff',
                                                            backdropFilter: 'blur(10px)',
                                                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'}`,
                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                transform: 'translateY(-6px)',
                                                                boxShadow: isDark ? '0 20px 40px rgba(0,0,0,0.6)' : '0 15px 35px rgba(0,0,0,0.06)'
                                                            }
                                                        }}
                                                        onClick={() => handleClientClick(client)}
                                                    >
                                                        <Box sx={{ p: 3 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                                <Avatar sx={{ 
                                                                    width: 44, height: 44, 
                                                                    background: `linear-gradient(135deg, #6366f1 0%, #a855f7 100%)`,
                                                                    fontWeight: 700 
                                                                }}>
                                                                    {client.name.charAt(0).toUpperCase()}
                                                                </Avatar>
                                                                <Box sx={{ ml: 2 }}>
                                                                    <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>{client.name}</Typography>
                                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{client.email}</Typography>
                                                                </Box>
                                                            </Box>
                                                            <Stack spacing={1} sx={{ mt: 2 }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Reservas Totales</Typography>
                                                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{client.stats.totalReservations}</Typography>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Tasa Cancelación</Typography>
                                                                    <Typography variant="body2" sx={{ fontWeight: 800, color: client.stats.cancellationRate > 25 ? '#ef4444' : '#10b981' }}>
                                                                        {client.stats.cancellationRate}%
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>
                                                        </Box>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </>
            )}

            {/* Client Detail Dialog */}
            <Dialog
                open={detailDialogOpen}
                onClose={handleCloseDetail}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: {
                        borderRadius: 5,
                        backgroundColor: isDark ? 'rgba(30,30,30,0.8)' : '#ffffff',
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                        boxShadow: isDark ? '0 24px 64px rgba(0,0,0,0.8)' : '0 15px 45px rgba(0,0,0,0.15)',
                        overflow: 'hidden'
                    }
                }}
            >
                {selectedClient && (
                    <>
                        {/* Premium Header */}
                        <Box sx={{
                            p: 4,
                            pb: 6,
                            background: isDark ? 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                            position: 'relative',
                            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Avatar
                                    sx={{
                                        width: 80, height: 80,
                                        background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                                        boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
                                        fontSize: '2rem',
                                        fontWeight: 800
                                    }}
                                >
                                    {selectedClient.name.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.5px' }}>
                                        {selectedClient.name}
                                    </Typography>
                                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                        <Chip 
                                            label={selectedClient.business?.name || 'Cliente Individual'} 
                                            size="small" 
                                            icon={<BusinessIcon sx={{ fontSize: '14px !important' }} />}
                                            sx={{ fontWeight: 600, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }} 
                                        />
                                        <Chip 
                                            label={`Miembro desde ${format(new Date(selectedClient.createdAt), 'MMM yyyy', { locale: es })}`} 
                                            size="small" 
                                            sx={{ fontWeight: 500, opacity: 0.8 }} 
                                        />
                                    </Stack>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<EventAvailableIcon />}
                                    onClick={(e) => handleOpenAddRes(e, selectedClient)}
                                    sx={{ 
                                        borderRadius: 3, 
                                        fontWeight: 800,
                                        bgcolor: '#10b981',
                                        '&:hover': { bgcolor: '#059669' },
                                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                                    }}
                                >
                                    Nueva Reserva
                                </Button>
                                <IconButton 
                                    onClick={handleCloseDetail} 
                                    sx={{ 
                                        bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        <DialogContent sx={{ p: 5, mt: -4, backgroundColor: 'transparent' }}>
                            <Box sx={{ 
                                bgcolor: isDark ? 'rgba(40,40,40,0.6)' : '#fff', 
                                borderRadius: 4,
                                p: 1,
                                mb: 4,
                                display: 'inline-flex',
                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                                boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 15px rgba(0,0,0,0.05)'
                            }}>
                                <Tabs 
                                    value={detailTab} 
                                    onChange={(_, v) => setDetailTab(v)}
                                    sx={{
                                        '& .MuiTabs-indicator': { display: 'none' },
                                        '& .MuiTab-root': {
                                            borderRadius: 3,
                                            minHeight: 44,
                                            fontWeight: 700,
                                            transition: 'all 0.3s',
                                            '&.Mui-selected': {
                                                bgcolor: '#6366f1',
                                                color: '#fff',
                                                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
                                            }
                                        }
                                    }}
                                >
                                    <Tab icon={<PersonIcon sx={{ fontSize: 18, mr: 1 }} />} iconPosition="start" label="Información" />
                                    <Tab icon={<HistoryIcon sx={{ fontSize: 18, mr: 1 }} />} iconPosition="start" label="Historial" />
                                    <Tab icon={<BarChartIcon sx={{ fontSize: 18, mr: 1 }} />} iconPosition="start" label="Estadísticas" />
                                </Tabs>
                            </Box>

                            {/* Tab 0: Premium Information Form */}
                            {detailTab === 0 && (
                                <Box>
                                    <Grid container spacing={4}>
                                        {[
                                            { label: 'Email', value: selectedClient.email, field: 'email', icon: <EmailIcon /> },
                                            { label: 'Teléfono', value: selectedClient.phone, field: 'phone', icon: <PhoneIcon /> },
                                            { label: 'Teléfono Alternativo', value: selectedClient.alternative_phone, field: 'alternative_phone', icon: <PhoneIcon /> },
                                            { label: 'DNI', value: selectedClient.dni, field: 'dni', icon: <IDBadgeIcon /> },
                                            { label: 'Dirección', value: selectedClient.address, field: 'address', icon: <LocationIcon /> }
                                        ].map((item, idx) => (
                                            <Grid size={{ xs: 12, md: 6 }} key={idx}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {item.icon} {item.label}
                                                </Typography>
                                                {editing ? (
                                                    <TextField
                                                        fullWidth
                                                        variant="outlined"
                                                        value={editClient?.[item.field as keyof Client] || ''}
                                                        onChange={(e) => setEditClient(prev => prev ? { ...prev, [item.field]: e.target.value } : null)}
                                                        sx={{ 
                                                            mt: 1,
                                                            '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: isDark ? 'rgba(0,0,0,0.2)' : '#fff' } 
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600 }}>
                                                        {item.value || 'No especificado'}
                                                    </Typography>
                                                )}
                                            </Grid>
                                        ))}
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                                                Notas del Cliente
                                            </Typography>
                                            {editing ? (
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={4}
                                                    value={editClient?.notes || ''}
                                                    onChange={(e) => setEditClient(prev => prev ? { ...prev, notes: e.target.value } : null)}
                                                    sx={{ 
                                                        mt: 1,
                                                        '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: isDark ? 'rgba(0,0,0,0.2)' : '#fff' } 
                                                    }}
                                                />
                                            ) : (
                                                <Paper sx={{ p: 2, borderRadius: 3, bgcolor: isDark ? 'rgba(0,0,0,0.1)' : '#f8fafc', border: '1px dashed rgba(0,0,0,0.1)', mt: 1 }}>
                                                    <Typography variant="body2">{selectedClient.notes || 'Sin notas adicionales.'}</Typography>
                                                </Paper>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* Tab 1: Premium History Table */}
                            {detailTab === 1 && (
                                <Box sx={{ borderRadius: 4, overflow: 'hidden', border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                                    <TableContainer>
                                        <Table sx={{ minWidth: 650 }}>
                                            <TableHead sx={{ bgcolor: isDark ? 'rgba(0,0,0,0.3)' : '#f8fafc' }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 800 }}>Fecha y Hora</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>Tablero / Recurso</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>Estado</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }} align="right">Acciones</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                            {selectedClient.reservations && selectedClient.reservations.length > 0 ? (
                                                selectedClient.reservations.map((res) => (
                                                        <TableRow key={res.id} sx={{ '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' } }}>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                    {format(new Date(res.startDate), "d 'de' MMMM", { locale: es })}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                                                    {format(new Date(res.startDate), 'HH:mm')} - {format(new Date(res.endDate), 'HH:mm')}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip label={res.board?.name || 'General'} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip 
                                                                    label={res.status === 'confirmed' ? 'Confirmada' : res.status === 'cancelled' ? 'Cancelada' : 'Pendiente'}
                                                                    size="small"
                                                                    color={getStatusColor(res.status) as any}
                                                                    sx={{ fontWeight: 700, borderRadius: 1 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <IconButton size="small" onClick={() => handleEditClick(res)} sx={{ color: 'primary.main' }}>
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={4} align="center" sx={{ py: 4, opacity: 0.6 }}>No hay reservas registradas.</TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {/* Tab 2: Premium Statistics Visualization */}
                            {detailTab === 2 && (
                                <Grid container spacing={3}>
                                    {[
                                        { label: 'Reservas Totales', value: selectedClient.stats.totalReservations, icon: <EventIcon />, color: '#6366f1' },
                                        { label: 'Confirmadas', value: selectedClient.stats.confirmedReservations, icon: <CheckCircleIcon />, color: '#10b981' },
                                        { label: 'Pendientes', value: selectedClient.stats.pendingReservations, icon: <HourglassEmptyIcon />, color: '#f59e0b' },
                                        { label: 'Canceladas', value: selectedClient.stats.cancelledReservations, icon: <CancelIcon />, color: '#ef4444' }
                                    ].map((stat, idx) => (
                                        <Grid size={{ xs: 6, sm: 3 }} key={idx}>
                                            <Paper sx={{ 
                                                p: 3, 
                                                borderRadius: 4, 
                                                textAlign: 'center', 
                                                bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                                                height: '100%',
                                                display: 'flex', flexDirection: 'column', alignItems: 'center'
                                            }}>
                                                <Avatar sx={{ bgcolor: `${stat.color}15`, color: stat.color, mb: 1.5 }}>{stat.icon}</Avatar>
                                                <Typography variant="h4" sx={{ fontWeight: 900 }}>{stat.value}</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>{stat.label}</Typography>
                                            </Paper>
                                        </Grid>
                                    ))}
                                    <Grid size={{ xs: 12 }}>
                                        <Paper sx={{ 
                                            p: 4, 
                                            borderRadius: 4, 
                                            bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                <Typography sx={{ fontWeight: 800 }}>Tasa de Cancelación</Typography>
                                                <Typography sx={{ fontWeight: 900, color: selectedClient.stats.cancellationRate > 25 ? '#ef4444' : '#10b981' }}>
                                                    {selectedClient.stats.cancellationRate}%
                                                </Typography>
                                            </Box>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={selectedClient.stats.cancellationRate} 
                                                sx={{ 
                                                    height: 12, borderRadius: 6,
                                                    bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                                    '& .MuiLinearProgress-bar': { 
                                                        borderRadius: 6,
                                                        background: `linear-gradient(90deg, #10b981 0%, ${selectedClient.stats.cancellationRate > 25 ? '#ef4444' : '#10b981'} 100%)` 
                                                    }
                                                }} 
                                            />
                                            <Typography variant="caption" sx={{ mt: 2, display: 'block', textAlign: 'center', opacity: 0.6 }}>
                                                Una tasa superior al 30% se considera crítica para la fidelidad.
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            )}
                        </DialogContent>

                        <DialogActions sx={{ p: 4, pt: 0, justifyContent: 'space-between' }}>
                            {detailTab === 0 && (
                                <>
                                    {!editing ? (
                                        <>
                                            <Button 
                                                color="error" 
                                                variant="text" 
                                                startIcon={<EventBusyIcon />}
                                                onClick={handleCancelFutureClick}
                                                sx={{ fontWeight: 700 }}
                                            >
                                                Cancelar Reservas Futuras
                                            </Button>
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <Button 
                                                    variant="contained" 
                                                    disableElevation
                                                    onClick={() => { setEditClient(selectedClient); setEditing(true); }}
                                                    startIcon={<EditIcon />}
                                                    sx={{ borderRadius: 3, fontWeight: 700, px: 4 }}
                                                >
                                                    Editar Perfil
                                                </Button>
                                                <Button 
                                                    variant="outlined" 
                                                    onClick={handleCloseDetail}
                                                    sx={{ borderRadius: 3, fontWeight: 700, px: 3 }}
                                                >
                                                    Cerrar
                                                </Button>
                                            </Box>
                                        </>
                                    ) : (
                                        <>
                                            <Button variant="text" onClick={() => { setEditClient(null); setEditing(false); }} sx={{ fontWeight: 700 }}>
                                                Descartar Cambios
                                            </Button>
                                            <Button 
                                                variant="contained" 
                                                color="success" 
                                                disableElevation
                                                startIcon={<CheckCircleIcon />}
                                                onClick={async () => {
                                                    if (!selectedClient || !editClient) return;
                                                    try {
                                                        const response = await axios.put(`/api/clients/${selectedClient.id}`, { ...selectedClient, ...editClient });
                                                        if (response.data) {
                                                            const updatedClient = { ...response.data, stats: { ...selectedClient.stats, ...(response.data.stats || {}) } };
                                                            setClients(prev => prev.map(c => c.id === selectedClient.id ? updatedClient : c));
                                                            setSelectedClient(updatedClient);
                                                        }
                                                        setEditClient(null);
                                                        setEditing(false);
                                                    } catch (error) {
                                                        console.error('Error updating client:', error);
                                                        alert('Error al actualizar cliente');
                                                    }
                                                }}
                                                sx={{ borderRadius: 3, fontWeight: 700, px: 4, bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                                            >
                                                Guardar Cambios
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                            {detailTab !== 0 && (
                                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button variant="outlined" onClick={handleCloseDetail} sx={{ borderRadius: 3, fontWeight: 700, px: 4 }}>
                                        Cerrar Panel
                                    </Button>
                                </Box>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Add Reservation Popover */}
            <AddReservationPopover
                anchorEl={addResAnchor}
                open={Boolean(addResAnchor)}
                onClose={() => setAddResAnchor(null)}
                onAddSuccess={() => {
                    setAddResAnchor(null);
                    refreshSelectedClient();
                }}
                boards={availableBoards}
                initialDate={new Date()}
                initialClient={selectedClient ? {
                    name: selectedClient.name,
                    email: selectedClient.email,
                    phone: selectedClient.phone
                } : undefined}
            />

            {/* Reservation Edit Dialog */}
            {
                selectedClient && (
                    <ReservationEditDialog
                        open={editDialogOpen}
                        reservation={editReservation as any} // Type casting because IDs might be number vs string
                        businessId={selectedClient.businessId.toString()}
                        board={editReservation?.board ? {
                            ...editReservation.board,
                            id: editReservation.board.id.toString()
                        } : null}
                        onClose={handleEditClose}
                        onSave={handleEditSave}
                    />
                )
            }
            {/* Cancel Future Reservations Confirmation Dialog */}
            <Dialog
                open={cancelFutureDialogOpen}
                onClose={() => setCancelFutureDialogOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        p: 1,
                        bgcolor: isDark ? 'rgba(30,30,30,0.95)' : '#fff',
                        backdropFilter: 'blur(10px)'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800 }}>¿Confirmar Cancelación Masiva?</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.8 }}>
                        Esta acción cancelará todas las reservas pendientes y confirmadas a partir de hoy para este cliente.
                        Se enviará una notificación automática por WhatsApp.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Motivo de la cancelación"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        multiline
                        rows={3}
                        placeholder="Ej: Mantenimiento, cierre por feriado..."
                        variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setCancelFutureDialogOpen(false)} sx={{ fontWeight: 700 }}>Ignorar</Button>
                    <Button 
                        onClick={handleConfirmCancelFuture} 
                        color="error" 
                        variant="contained" 
                        disableElevation
                        sx={{ borderRadius: 3, px: 3, fontWeight: 700 }}
                    >
                        Confirmar y Notificar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container >
    );
};

export default Clients;
